import { Component, OnInit, OnDestroy, inject, input, computed, signal, effect, output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { Subject } from "rxjs";

import saveAs from "@shared/utils/fileSaver.js";
import { SelectOption } from "@shared/models/select-option.interface";
import { MilestoneGroup, PayCycle } from "@shared/models/pay-cycle.interface";
import { InputAutocompleteModule } from "@shared/components/input-autocomplete/input-autocomplete.module";
import { InputWrapperModule } from "@shared/components/input-wrapper/input-wrapper.module";
import { FundingService } from "src/app/standalone/funding-date/services/funding.service";
import { ToastService } from "@shared/services/toast/toast.service";
import { IconModule } from "@shared/components/icon/icon.module";
import { MilestoneDownloadedOption } from "src/app/standalone/funding-date/model/funding-details.model";

export interface ReportGroup {
	value: string;
	text: string;
}

@Component({
	selector: "app-download-combined-report",
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MatButtonModule,
		MatIconModule,
		InputAutocompleteModule,
		InputWrapperModule,
		IconModule
	],
	templateUrl: "./download-combined-report.component.html",
	styleUrls: ["./download-combined-report.component.scss"]
})
export class DownloadCombinedReportComponent implements OnInit, OnDestroy {
	payCycle = input<PayCycle | undefined>();
	milestoneGroups = input<MilestoneGroup[]>([]);
	disableDownload = signal(false);
	downloadForm!: FormGroup;
	isLoading = false;
	resetForm = input<boolean>(false);
	resetDownloadReportDropdownForm = output<boolean>();

	private fb = inject(FormBuilder);
	private destroy$ = new Subject<void>();
	private fundingService = inject(FundingService);
	private toastService = inject(ToastService);

	optionsByGroup = signal<any>({} as MilestoneDownloadedOption);

	optionEffect = effect(
		() => {
			if (this.payCycle()) {
				this.fetchDownloadOptions();
			}
			if (this.resetForm()) {
				this.downloadForm.reset();
				this.fetchDownloadOptions();
				this.resetDownloadReportDropdownForm.emit(false);
			}
		},
		{ allowSignalWrites: true }
	);

	milestoneGroupsName = computed(() =>
		this.milestoneGroups()
			?.filter(milestoneGroup =>
				Object.keys(this.optionsByGroup()?.optionsByGroup).includes(milestoneGroup?.group)
			)
			.map(milestoneGroup => {
				if (milestoneGroup && this.optionsByGroup()?.optionsByGroup[milestoneGroup?.group]) {
					return {
						text: `${milestoneGroup.group} Payments`,
						value: milestoneGroup.group,
						disabled: !this.optionsByGroup().optionsByGroup[milestoneGroup.group].downloadable,
						downloadable: this.optionsByGroup().optionsByGroup[milestoneGroup.group].downloadable,
						alreadyDownloaded: this.optionsByGroup().optionsByGroup[milestoneGroup.group].alreadyDownloaded,
						downloadedAt: this.optionsByGroup().optionsByGroup[milestoneGroup.group].downloadedAt
					} as SelectOption;
				} else {
					return {
						text: `${milestoneGroup.group} Payments`,
						value: milestoneGroup.group,
						disabled: true
					} as SelectOption;
				}
			})
	);

	ngOnInit(): void {
		this.initializeForm();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	downloadPdf(): void {
		if (this.downloadForm.valid) {
			this.isLoading = true;
			const selectedGroups = this.downloadForm.get("selectedGroups")?.value;

			this.fundingService.getFundingPdf(this.payCycle()!.id, selectedGroups).subscribe({
				next: (buffer: Blob) => {
					const data: Blob = new Blob([buffer], {
						type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
					});
					saveAs(data, `report_funding_${this.payCycle()!.id}_${selectedGroups.join("_")}.pdf`);
					this.disableDownload.set(false);
				},
				error: () =>
					this.toastService.showError("Unable to generate PDF. Please contact support or try again later."),
				complete: () => this.fetchDownloadOptions()
			});

			setTimeout(() => {
				this.isLoading = false;
			}, 2000);

			this.resetDownloadReportDropdownForm.emit(true);
		}
	}

	private initializeForm(): void {
		this.downloadForm = this.fb.group({
			selectedGroups: [[], Validators.required]
		});
	}

	private fetchDownloadOptions(): void {
		this.fundingService.getAlreadyDownloadedMilestoneOptions(this.payCycle()?.id).subscribe(data => {
			this.optionsByGroup.set(data);
		});
	}
}
